/**
 * Viewport padding helpers for site plan visibility tolerance.
 *
 * When site plans are active (zoom >= MIN_SITE_PLAN_ZOOM), a site plan polygon may
 * remain partially visible in the viewport even if its associated result marker falls
 * slightly outside the current rectangular-area search bounds.
 *
 * These helpers expand the fetch bounds by a small fixed geographic tolerance so
 * partially-visible site plans near viewport edges are not prematurely removed
 * from results during Search Within Map flows.
 *
 * Design constraints:
 * - Uses a constant ~1 mile geographic tolerance for predictable behavior.
 * - Applies ONLY to rectangular-area search flows.
 * - Pure functions — no side effects, no SDK coupling.
 *
 * @module searchResultsMap/services/sitePlanViewportPadding
 */

import { SITE_PLAN_RENDER_ZOOM_THRESHOLD } from "../constants";

/**
 * Constant viewport expansion in decimal degrees.
 *
 * ~0.015° latitude ≈ 1 mile / 1.6 km.
 * Longitude distance varies slightly by latitude.
 *
 * The padding is intentionally small:
 * enough to preserve partially-visible site plans near viewport edges,
 * while avoiding excessive overfetching outside the visible map area.
 */
const SITE_PLAN_VIEWPORT_PADDING = 0.015;

/**
 * Returns the viewport padding amount in decimal degrees.
 * Padding is only applied when site plans are active.
 *
 * @param {number} zoom
 * @returns {number} Padding in decimal degrees
 */
export function getViewportPaddingForSitePlans(zoom) {
  if (zoom < SITE_PLAN_RENDER_ZOOM_THRESHOLD) {
    return 0;
  }

  return SITE_PLAN_VIEWPORT_PADDING;
}

/**
 * Expands viewport bounds by the provided padding amount.
 * Returns a new bounds object; does not mutate the input.
 *
 * @param {{ south: number, north: number, west: number, east: number }} bounds
 * @param {number} padding - Expansion amount in decimal degrees
 * @returns {{ south: number, north: number, west: number, east: number }}
 */
export function expandBounds(bounds, padding) {
  if (!padding || !bounds) {
    return bounds;
  }

  return {
    south: bounds.south - padding,
    north: bounds.north + padding,
    west: bounds.west - padding,
    east: bounds.east + padding,
  };
}